import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import Layout from "layouts/DashboardManage";
import axios from "helpers/_axios";
import Notification, { notifyApiError } from "components/Notification";
import SubmitButton from "components/Forms/Buttons/Submit";
import { SpinnerSyncBlock } from "components/Spinner";
import Translate from "helpers/Translate";
import AiChipIcon from "./AiChipIcon";

import "./Ai.styles.scss";

const EMPTY_FORM = {
  provider: "",
  model: "",
  api_key: "",
  ollama_url: "",
  lens_ai_domain: "",
  api_key_set: false,
  api_key_masked: ""
};

function Field({
  id,
  label,
  required,
  help,
  icon,
  trailing,
  className,
  children
}) {
  return (
    <div className={`ai-field ${className || ""}`.trim()}>
      <label htmlFor={id} className="ai-field__label">
        {label}
        {required && <span className="ai-field__required">*</span>}
      </label>
      <div className="ai-field__control">
        {icon && (
          <i className={`ai-field__icon ${icon}`} aria-hidden="true"></i>
        )}
        {children}
        {trailing}
      </div>
      {help && <p className="ai-field__help">{help}</p>}
    </div>
  );
}

function AiForm({ match, history }) {
  const editId = match.params.id;
  const isEditing = Boolean(editId);

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    if (!editId) {
      setForm(EMPTY_FORM);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await axios().get(`/manage-ai/${editId}`);
        const data = result.data?.data || {};
        if (!cancelled) {
          setForm({
            provider: data.provider || "",
            model: data.model || "",
            api_key: "",
            ollama_url: data.ollama_url || "",
            lens_ai_domain: data.lens_ai_domain || "",
            api_key_set: !!data.api_key_set,
            api_key_masked: data.api_key_masked || ""
          });
        }
      } catch (error) {
        notifyApiError(error);
        history.push("/manage/ai");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editId, history]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === "provider") {
        if (value === "ollama") {
          next.api_key = "";
        } else {
          next.ollama_url = "";
        }
      }
      return next;
    });
  };

  const handleSubmit = async e => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.provider || !form.model || !form.lens_ai_domain) {
      Notification.error(Translate({ id: "please_fill_required_fields" }));
      return;
    }
    if (form.provider === "ollama") {
      if (!form.ollama_url || !form.ollama_url.trim()) {
        Notification.error(Translate({ id: "please_fill_required_fields" }));
        return;
      }
    } else if (!form.api_key_set && !(form.api_key && form.api_key.trim())) {
      Notification.error(Translate({ id: "please_fill_required_fields" }));
      return;
    }
    try {
      setSaving(true);
      const payload = {
        provider: form.provider,
        model: form.model,
        lens_ai_domain: form.lens_ai_domain
      };
      if (form.provider === "ollama") {
        payload.ollama_url = form.ollama_url.trim();
        payload.api_key = "";
      } else {
        payload.ollama_url = "";
        if (form.api_key && form.api_key.trim()) {
          payload.api_key = form.api_key.trim();
        }
      }
      if (isEditing) {
        await axios().put(`/manage-ai/${editId}`, payload);
        Notification.success(
          Translate({ id: "ai_credentials_saved_successfully" })
        );
      } else {
        await axios().post("/manage-ai", payload);
        Notification.success(Translate({ id: "ai_model_added_successfully" }));
      }
      history.push("/manage/ai");
    } catch (error) {
      notifyApiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      {loading && <SpinnerSyncBlock />}
      <div className="manage ai-credentials">
        <Link to="/manage/ai" className="back mb-20">
          <i className="fas fa-long-arrow-alt-left"></i>
          {Translate({ id: "back_to_manage_ai" })}
        </Link>

        <div className="manage-page-heading">
          <span
            className="manage-page-icon tone-teal ai-page-icon"
            aria-hidden="true"
          >
            <AiChipIcon className="ai-page-icon__svg" />
          </span>
          <div className="manage-page-heading__text">
            <h1 className="title">
              {isEditing
                ? Translate({ id: "edit_ai_model" })
                : Translate({ id: "add_ai_model" })}
            </h1>
            <p className="manage-page-subtitle">
              {Translate({ id: "ai_configuration_details_help" })}
            </p>
          </div>
        </div>

        {!loading && (
          <div className="ai-card">
            <div className="ai-card__header">
              <i
                className="fas fa-cog ai-card__header-icon"
                aria-hidden="true"
              ></i>
              <div>
                <h2 className="ai-card__title">
                  {Translate({ id: "ai_configuration_details" })}
                </h2>
                <p className="ai-card__subtitle">
                  {Translate({ id: "ai_configuration_details_help" })}
                </p>
              </div>
            </div>

            <div className="ai-card__grid">
              <Field
                id="provider"
                label={Translate({ id: "ai_provider" })}
                required
                help={Translate({ id: "ai_provider_help" })}
                icon="fas fa-link"
                trailing={
                  <i
                    className="fas fa-chevron-down ai-field__trailing"
                    aria-hidden="true"
                  ></i>
                }
              >
                <select
                  id="provider"
                  name="provider"
                  className="ai-field__input"
                  value={form.provider}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    {Translate({ id: "select" })}
                  </option>
                  <option value="groq">groq</option>
                  <option value="openai">openai</option>
                  <option value="ollama">ollama</option>
                </select>
              </Field>

              <Field
                id="model"
                label={Translate({ id: "ai_model" })}
                required
                help={Translate({ id: "ai_model_help" })}
                icon="fas fa-brain"
              >
                <input
                  id="model"
                  name="model"
                  type="text"
                  className="ai-field__input"
                  value={form.model}
                  onChange={handleChange}
                  placeholder="e.g. llama-3.1-8b-instant"
                />
              </Field>

              {form.provider !== "ollama" && (
                <Field
                  id="api_key"
                  label={Translate({ id: "ai_api_key" })}
                  required={!form.api_key_set}
                  help={
                    form.api_key_set
                      ? Translate({ id: "leave_blank_to_keep_existing_api_key" })
                      : Translate({ id: "ai_api_key_help" })
                  }
                  icon="fas fa-key"
                  trailing={
                    <button
                      type="button"
                      className="ai-field__eye"
                      onClick={() => setShowApiKey(prev => !prev)}
                      aria-label={showApiKey ? "Hide API key" : "Show API key"}
                    >
                      <i
                        className={`fas ${
                          showApiKey ? "fa-eye" : "fa-eye-slash"
                        }`}
                        aria-hidden="true"
                      ></i>
                    </button>
                  }
                >
                  <input
                    id="api_key"
                    name="api_key"
                    type={showApiKey ? "text" : "password"}
                    className="ai-field__input ai-field__input--trailing"
                    value={form.api_key}
                    onChange={handleChange}
                    placeholder={
                      form.api_key_set
                        ? form.api_key_masked
                        : Translate({ id: "enter_api_key" })
                    }
                    autoComplete="new-password"
                  />
                </Field>
              )}

              <Field
                id="lens_ai_domain"
                label={Translate({ id: "lens_ai_domain" })}
                required
                help={Translate({ id: "lens_ai_domain_help" })}
                icon="fas fa-link"
              >
                <input
                  id="lens_ai_domain"
                  name="lens_ai_domain"
                  type="text"
                  className="ai-field__input"
                  value={form.lens_ai_domain}
                  onChange={handleChange}
                  placeholder="http://127.0.0.1:3100"
                />
              </Field>

              {form.provider === "ollama" && (
                <Field
                  id="ollama_url"
                  label={Translate({ id: "ollama_url" })}
                  required
                  help={Translate({ id: "ollama_url_help" })}
                  icon="fas fa-server"
                >
                  <input
                    id="ollama_url"
                    name="ollama_url"
                    type="text"
                    className="ai-field__input"
                    value={form.ollama_url}
                    onChange={handleChange}
                    placeholder="http://127.0.0.1:11434"
                  />
                </Field>
              )}
            </div>

            <div className="ai-card__actions save-cancel-buttons">
              <Link to="/manage/ai" className="btn manage-btn-cancel">
                <i className="far fa-times-circle mr-10"></i>
                {Translate({ id: "cancel" })}
              </Link>
              <SubmitButton
                className="btn btn-primary manage-btn-save"
                buttonIconClass="fas fa-save mr-10"
                buttonText={Translate({ id: "save_changes" })}
                btnId="save-ai-credentials"
                isLoading={saving}
                handleSubmit={handleSubmit}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default withRouter(AiForm);
